"use client"

import { motion } from "motion/react"
import { useTranslations } from "next-intl"
import { useMemo } from "react"
import { FlickeringGrid } from "@/components/magicui/flickering-grid"
import Safari from "@/components/magicui/safari"
import { config } from "@/config"
import { cn } from "@/utils/cn"
import { Section, type SectionProps } from "./section"

interface SolutionSectionProps extends Partial<SectionProps> {}

export function SolutionSection({
  title: _title,
  description: _description,
  tagline: _tagline,
  className,
  ...props
}: SolutionSectionProps) {
  const t = useTranslations("Sections.Solution")

  const title = _title ?? t("title")
  const description = _description ?? t("description")
  const tagline = _tagline ?? t("tagline")

  const features = useMemo(
    () => [
      {
        title: t("features.0.title"),
        description: t("features.0.description"),
        className: "dark:hidden hover:bg-primary/10 transition-all duration-500 ease-out",
        content: (
          <>
            <Safari
              src={t("features.0.imageLight")}
              url={config.site.url}
              className="-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]"
            />
          </>
        ),
      },
      {
        title: t("features.0.title"),
        description: t("features.0.description"),
        className: "hidden dark:block hover:bg-primary/10 transition-all duration-500 ease-out",
        content: (
          <>
            <Safari
              src={t("features.0.imageDark")}
              url={config.site.url}
              className="-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]"
            />
          </>
        ),
      },
      {
        title: t("features.1.title"),
        description: t("features.1.description"),
        className:
          "order-3 lg:order-0 dark:hidden hover:bg-primary/10 transition-all duration-500 ease-out",
        content: (
          <Safari
            src={t("features.1.imageLight")}
            url={config.site.url}
            className="-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]"
          />
        ),
      },
      {
        title: t("features.1.title"),
        description: t("features.1.description"),
        className:
          "order-3 lg:order-0 hidden dark:block hover:bg-primary/10 transition-all duration-500 ease-out",
        content: (
          <Safari
            src={t("features.1.imageDark")}
            url={config.site.url}
            className="-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]"
          />
        ),
      },
      {
        title: t("features.2.title"),
        description: t("features.2.description"),
        className:
          "md:row-span-2 dark:hidden hover:bg-primary/10 transition-all duration-500 ease-out",
        content: (
          <>
            <FlickeringGrid
              className="absolute inset-0 z-0 [mask:radial-gradient(circle_at_center,#fff_400px,transparent_0)]"
              squareSize={4}
              gridGap={6}
              color="#000"
              maxOpacity={0.1}
              flickerChance={0.1}
              height={800}
              width={800}
            />
            <Safari
              src={t("features.2.imageLight")}
              url={config.site.url}
              className="-mb-48 mt-16 ml-12 h-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-x-[-10px]"
            />
          </>
        ),
      },
      {
        title: t("features.2.title"),
        description: t("features.2.description"),
        className:
          "md:row-span-2 hidden dark:block hover:bg-primary/10 transition-all duration-500 ease-out",
        content: (
          <>
            <FlickeringGrid
              className="absolute inset-0 z-0 [mask:radial-gradient(circle_at_center,#fff_400px,transparent_0)]"
              squareSize={4}
              gridGap={6}
              color="#fff"
              maxOpacity={0.1}
              flickerChance={0.1}
              height={800}
              width={800}
            />
            <Safari
              src={t("features.2.imageDark")}
              url={config.site.url}
              className="-mb-48 mt-16 ml-12 h-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-x-[-10px]"
            />
          </>
        ),
      },
      {
        title: t("features.3.title"),
        description: t("features.3.description"),
        className:
          "order-4 md:col-span-2 dark:hidden hover:bg-primary/10 transition-all duration-500 ease-out lg:order-0",
        content: (
          <>
            <Safari
              src={t("features.3.imageLight")}
              url={config.site.url}
              className="-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]"
            />
          </>
        ),
      },
      {
        title: t("features.3.title"),
        description: t("features.3.description"),
        className:
          "order-4 md:col-span-2 hidden dark:block hover:bg-primary/10 transition-all duration-500 ease-out lg:order-0",
        content: (
          <>
            <Safari
              src={t("features.3.imageDark")}
              url={config.site.url}
              className="-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]"
            />
          </>
        ),
      },
    ],
    [t],
  )

  return (
    <Section
      id="solution"
      tagline={tagline}
      title={title}
      description={description}
      className={cn("px-2", className)}
      {...props}
    >
      <div className="mx-auto grid max-w-sm grid-cols-1 gap-6 text-muted-foreground md:max-w-3xl md:grid-cols-2 md:grid-rows-3 lg:max-w-6xl lg:auto-rows-fr lg:grid-cols-3 lg:grid-rows-2 xl:max-w-6xl">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            className={cn(
              "group relative items-start overflow-hidden rounded-2xl border bg-card p-4",
              feature.className,
            )}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              stiffness: 100,
              damping: 30,
              delay: index * 0.1,
            }}
            viewport={{ once: true }}
          >
            <div>
              <h3 className="mb-2 font-semibold text-primary">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
            {feature.content}
            <div className="pointer-events-none absolute bottom-0 left-0 h-32 w-full bg-linear-to-t from-neutral-50 dark:from-neutral-900" />
          </motion.div>
        ))}
      </div>
    </Section>
  )
}
